"use strict";
/**
 * @fileoverview Nexus自动文件监听器 - 高性能零配置文件监听系统
 * @description 提供高性能的文件监听功能，包含防抖机制、智能过滤、性能监控和内存优化
 *
 * @author ESEngine Team
 * @version 2.1.0
 * @since 1.0.0
 * @license MIT
 *
 * @example
 * ```typescript
 * const watcher = NexusAutoWatcher.getInstance();
 * watcher.start();
 *
 * // 获取性能指标
 * const metrics = watcher.getPerformanceMetrics();
 * console.log('监听性能:', metrics);
 *
 * // 自定义过滤规则
 * watcher.addFilterRule(/\.test\.ts$/, 'exclude', 10);
 * ```
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutoWatcher = exports.NexusAutoWatcher = exports.SmartFileFilter = void 0;
const fs = __importStar(require("fs"));
const path = __importStar(require("path"));
/**
 * 智能文件过滤器
 *
 * @description 提供基于规则的文件过滤功能，支持自定义过滤条件和优先级
 * @class SmartFileFilter
 * @since 2.1.0
 */
class SmartFileFilter {
    /**
     * 构造函数，初始化默认过滤规则
     *
     * @since 2.1.0
     */
    constructor() {
        this.filterRules = [];
        this.cache = new Map();
        this.cacheHits = 0;
        this.cacheTotal = 0;
        this.initializeDefaultRules();
    }
    /**
     * 初始化默认过滤规则
     *
     * @private
     * @since 2.1.0
     */
    initializeDefaultRules() {
        // 包含规则
        this.addRule(/\.ts$/, 'include', 100, 'TypeScript源文件');
        this.addRule(/\.js$/, 'include', 90, 'JavaScript源文件');
        this.addRule(/\.json$/, 'include', 80, 'JSON配置文件');
        // 排除规则
        this.addRule(/node_modules/, 'exclude', 200, '排除node_modules');
        this.addRule(/\.d\.ts$/, 'exclude', 150, '排除类型声明文件');
        this.addRule(/\.git/, 'exclude', 180, '排除Git文件');
        this.addRule(/dist\/|build\/|out\//, 'exclude', 170, '排除构建输出');
        this.addRule(/\.log$/, 'exclude', 160, '排除日志文件');
        this.addRule(/\.tmp$|\.temp$/, 'exclude', 140, '排除临时文件');
    }
    /**
     * 添加过滤规则
     *
     * @param pattern 正则表达式模式
     * @param action 动作类型
     * @param priority 优先级（数字越大优先级越高）
     * @param description 规则描述
     *
     * @since 2.1.0
     */
    addRule(pattern, action, priority = 0, description) {
        this.filterRules.push({ pattern, action, priority, description });
        this.filterRules.sort((a, b) => b.priority - a.priority);
        this.clearCache();
    }
    /**
     * 移除过滤规则
     *
     * @param pattern 要移除的规则模式
     * @returns 是否成功移除
     *
     * @since 2.1.0
     */
    removeRule(pattern) {
        const index = this.filterRules.findIndex(rule => rule.pattern.source === pattern.source);
        if (index > -1) {
            this.filterRules.splice(index, 1);
            this.clearCache();
            return true;
        }
        return false;
    }
    /**
     * 检查文件是否应该被处理
     *
     * @param filePath 文件路径
     * @returns 是否应该处理
     *
     * @since 2.1.0
     */
    shouldProcess(filePath) {
        this.cacheTotal++;
        // 检查缓存
        if (this.cache.has(filePath)) {
            this.cacheHits++;
            return this.cache.get(filePath);
        }
        // 应用过滤规则
        let result = false; // 默认不处理
        for (const rule of this.filterRules) {
            if (rule.pattern.test(filePath)) {
                result = rule.action === 'include';
                break;
            }
        }
        // 缓存结果
        this.cache.set(filePath, result);
        // 限制缓存大小
        if (this.cache.size > 1000) {
            this.clearCache();
        }
        return result;
    }
    /**
     * 获取缓存命中率
     *
     * @returns 缓存命中率 (0-1)
     *
     * @since 2.1.0
     */
    getCacheHitRate() {
        return this.cacheTotal > 0 ? this.cacheHits / this.cacheTotal : 0;
    }
    /**
     * 清理缓存
     *
     * @since 2.1.0
     */
    clearCache() {
        this.cache.clear();
        this.cacheHits = 0;
        this.cacheTotal = 0;
    }
    /**
     * 获取所有过滤规则
     *
     * @returns 过滤规则数组
     *
     * @since 2.1.0
     */
    getRules() {
        return [...this.filterRules];
    }
}
exports.SmartFileFilter = SmartFileFilter;
/**
 * Nexus自动文件监听器
 *
 * @description 提供高性能的零配置文件监听功能，包含：
 * - 防抖机制和性能优化
 * - 智能文件过滤系统
 * - 内存管理和监控
 * - 详细的性能指标
 *
 * @class NexusAutoWatcher
 * @since 1.0.0
 * @version 2.1.0
 *
 * @example
 * ```typescript
 * const watcher = NexusAutoWatcher.getInstance();
 *
 * // 启动监听
 * watcher.start();
 *
 * // 添加自定义过滤规则
 * watcher.addFilterRule(/\.spec\.ts$/, 'exclude', 120);
 *
 * // 获取性能指标
 * const metrics = watcher.getPerformanceMetrics();
 * console.log('平均响应时间:', metrics.averageResponseTime);
 *
 * // 停止监听
 * watcher.stop();
 * ```
 */
class NexusAutoWatcher {
    /**
     * 私有构造函数（单例模式）
     *
     * @private
     * @since 1.0.0
     */
    constructor() {
        this.watchers = [];
        this.debounceTimers = new Map();
        this.isWatching = false;
        this.processingQueue = new Map();
        /** 自动监听器配置 */
        this.config = {
            enabled: true,
            debounceTime: 300,
            autoStart: true,
            maxFiles: 10000,
            memoryThreshold: 100
        };
        this.fileFilter = new SmartFileFilter();
        this.performanceMetrics = this.initializeMetrics();
        this.setupMemoryMonitoring();
        if (this.config.autoStart && typeof process !== 'undefined') {
            setTimeout(() => this.start(), 1000);
        }
    }
    /**
     * 获取单例实例
     *
     * @returns NexusAutoWatcher实例
     * @since 1.0.0
     */
    static getInstance() {
        if (!NexusAutoWatcher.instance) {
            NexusAutoWatcher.instance = new NexusAutoWatcher();
        }
        return NexusAutoWatcher.instance;
    }
    /**
     * 初始化性能指标
     *
     * @private
     * @returns 初始化的性能指标
     * @since 2.1.0
     */
    initializeMetrics() {
        return {
            watchCount: 0,
            processCount: 0,
            filteredCount: 0,
            averageResponseTime: 0,
            lastTriggerTime: 0,
            memoryUsage: 0,
            watchedDirectories: 0,
            cacheHitRate: 0
        };
    }
    /**
     * 设置内存监控
     *
     * @private
     * @since 2.1.0
     */
    setupMemoryMonitoring() {
        if (typeof process !== 'undefined') {
            setInterval(() => {
                const memUsage = process.memoryUsage();
                this.performanceMetrics.memoryUsage = memUsage.heapUsed / 1024 / 1024;
                // 检查内存阈值
                if (this.performanceMetrics.memoryUsage > (this.config.memoryThreshold || 100)) {
                    this.performMemoryOptimization();
                }
            }, 5000);
        }
    }
    /**
     * 执行内存优化
     *
     * @private
     * @since 2.1.0
     */
    performMemoryOptimization() {
        console.warn('🔥 内存使用过高，执行优化...');
        // 清理防抖定时器
        this.clearAllTimers();
        // 清理文件过滤器缓存
        this.fileFilter.clearCache();
        // 清理处理队列
        this.processingQueue.clear();
        // 触发垃圾回收
        if (global.gc) {
            global.gc();
        }
        console.log('✅ 内存优化完成');
    }
    /**
     * 启动自动监听
     *
     * @description 在错误隔离环境中启动文件监听，确保监听器错误不会影响主程序
     * @public
     * @since 1.0.0
     */
    start() {
        if (!this.config.enabled || this.isWatching)
            return;
        console.log('🚀 Nexus自动监听器启动 - 零配置高性能文件监听！');
        try {
            const srcPath = this.findSourceDirectory();
            if (srcPath) {
                this.watchDirectory(srcPath);
                this.isWatching = true;
                this.performanceMetrics.watchedDirectories = 1;
                console.log(`👁️ 正在监听: ${srcPath}`);
                console.log('✨ 文件变化将自动触发重新生成，享受零配置开发体验！');
                console.log(`📊 性能优化已启用 - 防抖: ${this.config.debounceTime}ms`);
            }
        }
        catch (error) {
            console.error('❌ 启动自动监听失败:', error);
        }
    }
    /**
     * 停止监听
     *
     * @description 停止所有文件监听并清理资源
     * @public
     * @since 1.0.0
     */
    stop() {
        if (!this.isWatching)
            return;
        this.watchers.forEach(watcher => watcher.close());
        this.watchers = [];
        this.clearAllTimers();
        this.processingQueue.clear();
        this.isWatching = false;
        this.performanceMetrics.watchedDirectories = 0;
        console.log('🛑 Nexus自动监听器已停止');
    }
    /**
     * 查找源代码目录
     *
     * @private
     * @returns 源代码目录路径或null
     * @since 1.0.0
     */
    findSourceDirectory() {
        const possiblePaths = [
            path.join(process.cwd(), 'src'),
            path.join(process.cwd(), 'plugins'),
            path.join(process.cwd(), 'panels'),
            process.cwd()
        ];
        for (const dirPath of possiblePaths) {
            if (fs.existsSync(dirPath)) {
                return dirPath;
            }
        }
        return null;
    }
    /**
     * 监听目录
     *
     * @private
     * @param dirPath 目录路径
     * @since 1.0.0
     */
    watchDirectory(dirPath) {
        try {
            const watcher = fs.watch(dirPath, { recursive: true }, (eventType, filename) => {
                if (!filename)
                    return;
                const filePath = path.join(dirPath, filename);
                this.handleFileChange(filePath, eventType);
            });
            this.watchers.push(watcher);
        }
        catch (error) {
            console.error(`❌ 监听目录失败: ${dirPath}`, error);
        }
    }
    /**
     * 处理文件变化
     *
     * @private
     * @param filePath 文件路径
     * @param eventType 事件类型
     * @since 1.0.0
     */
    handleFileChange(filePath, eventType) {
        const startTime = performance.now();
        this.performanceMetrics.watchCount++;
        // 智能过滤
        if (!this.fileFilter.shouldProcess(filePath)) {
            this.performanceMetrics.filteredCount++;
            return;
        }
        // 防抖处理
        const timer = this.debounceTimers.get(filePath);
        if (timer)
            clearTimeout(timer);
        this.debounceTimers.set(filePath, setTimeout(() => {
            this.processFileChange(filePath, eventType, startTime);
            this.debounceTimers.delete(filePath);
        }, this.config.debounceTime));
    }
    /**
     * 处理文件变化（防抖后）
     *
     * @private
     * @param filePath 文件路径
     * @param eventType 事件类型
     * @param startTime 开始时间
     * @since 1.0.0
     */
    processFileChange(filePath, eventType, startTime) {
        try {
            // 检查文件是否存在
            if (!fs.existsSync(filePath)) {
                if (eventType === 'rename') {
                    console.log(`📁 文件已删除: ${path.basename(filePath)}`);
                }
                return;
            }
            const content = fs.readFileSync(filePath, 'utf-8');
            // 检查是否是Nexus文件
            if (this.isNexusFile(content)) {
                console.log(`🔄 检测到Nexus文件变化: ${path.basename(filePath)}`);
                this.regeneratePlugin(filePath, content);
                this.performanceMetrics.processCount++;
            }
            // 更新性能指标
            const endTime = performance.now();
            const responseTime = endTime - startTime;
            this.updatePerformanceMetrics(responseTime);
        }
        catch (error) {
            console.error(`❌ 处理文件变化失败: ${filePath}`, error);
        }
    }
    /**
     * 更新性能指标
     *
     * @private
     * @param responseTime 响应时间
     * @since 2.1.0
     */
    updatePerformanceMetrics(responseTime) {
        this.performanceMetrics.lastTriggerTime = Date.now();
        this.performanceMetrics.averageResponseTime =
            (this.performanceMetrics.averageResponseTime + responseTime) / 2;
        this.performanceMetrics.cacheHitRate = this.fileFilter.getCacheHitRate();
    }
    /**
     * 检查是否是Nexus文件
     *
     * @private
     * @param content 文件内容
     * @returns 是否是Nexus文件
     * @since 1.0.0
     */
    isNexusFile(content) {
        return content.includes('@Plugin') ||
            content.includes('@Panel') ||
            content.includes('extends NexusPlugin') ||
            content.includes('extends NexusPanel');
    }
    /**
     * 重新生成插件
     *
     * @private
     * @param filePath 文件路径
     * @param content 文件内容
     * @since 1.0.0
     */
    regeneratePlugin(filePath, content) {
        try {
            const pluginName = this.extractPluginName(content) || path.basename(filePath, '.ts');
            const outputDir = this.getOutputDirectory();
            if (!outputDir) {
                console.log('📝 未找到输出目录，跳过文件生成');
                return;
            }
            // 确保输出目录存在
            if (!fs.existsSync(outputDir)) {
                fs.mkdirSync(outputDir, { recursive: true });
            }
            // 生成主文件
            const mainContent = this.generateMainFile(content);
            const mainPath = path.join(outputDir, 'main.ts');
            fs.writeFileSync(mainPath, mainContent, 'utf-8');
            // 生成package.json
            const packageContent = this.generatePackageJson(pluginName);
            const packagePath = path.join(outputDir, 'package.json');
            fs.writeFileSync(packagePath, packageContent, 'utf-8');
            console.log(`✅ 自动重新生成完成: ${pluginName}`);
            console.log(`📁 输出目录: ${outputDir}`);
        }
        catch (error) {
            console.error('❌ 重新生成插件失败:', error);
        }
    }
    /**
     * 提取插件名称
     *
     * @private
     * @param content 文件内容
     * @returns 插件名称或null
     * @since 1.0.0
     */
    extractPluginName(content) {
        const match = content.match(/@Plugin\s*\(\s*\{[^}]*name\s*:\s*['"](.*?)['"][^}]*\}/);
        return match ? match[1] : null;
    }
    /**
     * 获取输出目录
     *
     * @private
     * @returns 输出目录路径或null
     * @since 1.0.0
     */
    getOutputDirectory() {
        const possibleDirs = [
            path.join(process.cwd(), 'dist'),
            path.join(process.cwd(), 'build'),
            path.join(process.cwd(), 'output'),
            path.join(process.cwd(), 'generated')
        ];
        for (const dir of possibleDirs) {
            if (fs.existsSync(dir)) {
                return dir;
            }
        }
        // 如果都不存在，创建默认的dist目录
        const defaultDir = path.join(process.cwd(), 'dist');
        return defaultDir;
    }
    /**
     * 生成主文件内容
     *
     * @private
     * @param sourceContent 源文件内容
     * @returns 生成的主文件内容
     * @since 1.0.0
     */
    generateMainFile(sourceContent) {
        const pluginName = this.extractPluginName(sourceContent) || 'UnknownPlugin';
        return `// 自动生成的插件主文件
// 生成时间: ${new Date().toISOString()}
// 源文件: ${sourceContent.split('\n')[0]}

import { NexusPlugin } from '@esengine/cocos-nexus';

${sourceContent}

// 导出插件实例
export default new ${pluginName}();
`;
    }
    /**
     * 生成package.json内容
     *
     * @private
     * @param pluginName 插件名称
     * @returns 生成的package.json内容
     * @since 1.0.0
     */
    generatePackageJson(pluginName) {
        const packageConfig = {
            name: pluginName.toLowerCase(),
            version: '1.0.0',
            description: `${pluginName} - Nexus Plugin`,
            main: './main.js',
            contributions: {
                profile: {
                    editor: {
                        label: pluginName,
                        icon: './icon.png'
                    }
                }
            },
            engines: {
                'cocos-creator': '>=3.3.0'
            },
            nexus: {
                generated: true,
                timestamp: new Date().toISOString()
            }
        };
        return JSON.stringify(packageConfig, null, 2);
    }
    /**
     * 清理所有定时器
     *
     * @private
     * @since 1.0.0
     */
    clearAllTimers() {
        for (const timer of this.debounceTimers.values()) {
            clearTimeout(timer);
        }
        this.debounceTimers.clear();
    }
    /**
     * 添加文件过滤规则
     *
     * @param pattern 正则表达式模式
     * @param action 过滤动作
     * @param priority 优先级
     * @param description 规则描述
     *
     * @public
     * @since 2.1.0
     */
    addFilterRule(pattern, action, priority = 0, description) {
        this.fileFilter.addRule(pattern, action, priority, description);
    }
    /**
     * 移除文件过滤规则
     *
     * @param pattern 要移除的规则模式
     * @returns 是否成功移除
     *
     * @public
     * @since 2.1.0
     */
    removeFilterRule(pattern) {
        return this.fileFilter.removeRule(pattern);
    }
    /**
     * 获取监听状态
     *
     * @returns 监听状态对象
     * @since 1.0.0
     */
    getStatus() {
        return {
            watching: this.isWatching,
            watchedDirectories: this.watchers.length,
            config: { ...this.config }
        };
    }
    /**
     * 获取性能监控数据
     *
     * @returns 性能监控数据
     * @since 2.1.0
     */
    getPerformanceMetrics() {
        return { ...this.performanceMetrics };
    }
    /**
     * 重置性能监控数据
     *
     * @since 2.1.0
     */
    resetPerformanceMetrics() {
        this.performanceMetrics = this.initializeMetrics();
        this.fileFilter.clearCache();
    }
    /**
     * 打印性能报告
     *
     * @since 2.1.0
     */
    printPerformanceReport() {
        const metrics = this.performanceMetrics;
        const efficiency = metrics.watchCount > 0 ?
            ((metrics.watchCount - metrics.filteredCount) / metrics.watchCount * 100).toFixed(1) : 0;
        console.group('🚀 NexusAutoWatcher 性能报告');
        console.log(`监听触发次数: ${metrics.watchCount}`);
        console.log(`文件处理次数: ${metrics.processCount}`);
        console.log(`过滤掉的文件: ${metrics.filteredCount}`);
        console.log(`过滤效率: ${efficiency}%`);
        console.log(`平均响应时间: ${metrics.averageResponseTime.toFixed(2)}ms`);
        console.log(`缓存命中率: ${(metrics.cacheHitRate * 100).toFixed(1)}%`);
        console.log(`内存使用量: ${metrics.memoryUsage.toFixed(2)}MB`);
        console.log(`监听目录数: ${metrics.watchedDirectories}`);
        console.groupEnd();
    }
    /**
     * 更新配置
     *
     * @param newConfig 新配置
     * @since 1.0.0
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
        // 如果监听状态发生变化，重新启动
        if (newConfig.enabled !== undefined) {
            if (newConfig.enabled && !this.isWatching) {
                this.start();
            }
            else if (!newConfig.enabled && this.isWatching) {
                this.stop();
            }
        }
    }
    /**
     * 获取所有过滤规则
     *
     * @returns 过滤规则数组
     * @since 2.1.0
     */
    getFilterRules() {
        return this.fileFilter.getRules();
    }
    /**
     * 清理缓存
     *
     * @since 2.1.0
     */
    clearCache() {
        this.fileFilter.clearCache();
    }
}
exports.NexusAutoWatcher = NexusAutoWatcher;
// 便捷导出
exports.AutoWatcher = NexusAutoWatcher.getInstance();
